// @ts-strict-ignore
import React, {
	useState,
	useEffect,
	useContext,
	Suspense
} from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";

import {
	AddNotificationAppDashboardMutation,
	AddNotificationAppDashboardMutationVariables,
	AddPatchUpdateNotificationAppDashboardMutation,
	AddPatchUpdateNotificationAppDashboardMutationVariables,
	GetAllKnightFrankReportsAppDashboardQuery,
	GetAllKnightFrankReportsAppDashboardQueryVariables
} from "app/GraphqlGeneratedTypes/types";
import {
	ADD_NOTIFICATION_GQL,
	ADD_PATCH_UPDATE_NOTIFICATION_GQL,
	GET_ALL_KF_REPORTS_GQL
} from "./AppDashboardQueries";

import { getNavLinks } from "app/utils/navLinksUtils";
import urlEnums from "app/utils/enum/urlEnums";
import { showError, showSuccess } from "app/utils/notificationUtils";
import ProgressBarContext from "app/context/ProgressBarContext";
import ActiveUserContext from "app/context/ActiveUserContext";
import { authorizationRoles } from "app/utils/authorizationUtils";
import useStatsigFeatureGate from "app/utils/customHooks/useStatsigFeatureGate";

import AppHeader from "app/pages/AppDashboard/components/AppHeader/AppHeader";
import AppFooter from "app/components/NewFooter/Footer";
import BreadcrumbsContextComponent from "app/pages/AppDashboard/BreadcrumbsContext";
import DefaultMetaTags from "app/components/MetaTags/DefaultMetaTags";

import classes from "./style.scss";

const PublicRoutes = React.lazy(() => import("app/pages/AppDashboard/routes/PublicRoutes"));
const AnalyticsPage = React.lazy(() => import("app/pages/Analytics/Analytics"));
const SuppliersRoutes = React.lazy(() => import("app/pages/Suppliers/SuppliersRoutes"));
const DashboardRoutes = React.lazy(() => import("app/pages/AppDashboard/routes/DashboardRoutes"));
const SettingsRoutes = React.lazy(() => import("app/pages/AppDashboard/routes/SettingsRoutes"));
const SiteSelector = React.lazy(() => import("app/pages/SiteSelector/SiteSelector"));
const AdminRoutes = React.lazy(() => import("app/pages/AppDashboard/routes/AdminRoutes"));
const PremiumSubscriberPolicyPage = React.lazy(() => import("app/pages/PremiumSubscriberPolicyPage/PremiumSubscriberPolicyPage"));
const AddNotificationPopup = React.lazy(() => import("app/components/AddNotificationPopup/AddNotificationPopup"));
const AppSideBar = React.lazy(() => import("app/pages/AppDashboard/components/AppSideBar/AppSideBar"));
const ProgressBar = React.lazy(() => import("app/components/ProgressBar/ProgressBar"));

let firstStageProgressBarLoading;
let secondStageProgressBarLoading;
let thirdStageProgressBarLoading;
let fourthStageProgressBarLoading;

const premiumPrivacyPolicyPath = "/premium-privacy-policy";
const resetPasswordPath = "/reset-password";

interface LocationState {
	returnUrl?: string;
	userWasRedirected?: string;
}

interface LocationFC extends LocationDOM {
	state: LocationState;
}

interface Props {
	location: LocationFC;
	history: HistoryDOM;
}

const AppDashboard: React.FC<Props> = (props) => {
	const [toggledMobileNav, setToggledMobileNav] = useState(false);
	const [navLinks, setNavLinks] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isPatchUpdateModalOpen, setIsPatchUpdateModalOpen] = useState(false);
	const [progressBarProps, setProgressBarProps] = useState({ progressPercentage: 0, transitionTime: 0, loadingNewPage: true });
	const [progressBarStyles, setProgressBarStyles] = useState(null);
	const [isPageLoaded, setIsPageLoaded] = useState(false);
	// This state is used if we are loading a separate section from a page insted of the whole page
	const [loadedSection, setLoadedSection] = useState(null);

	const getAllKFReportsQuery = useQuery<
	GetAllKnightFrankReportsAppDashboardQuery, GetAllKnightFrankReportsAppDashboardQueryVariables
	>(GET_ALL_KF_REPORTS_GQL);

	const [addNotification] = useMutation<
	AddNotificationAppDashboardMutation, AddNotificationAppDashboardMutationVariables
	>(ADD_NOTIFICATION_GQL);

	const [addPatchUpdateNotification] = useMutation<
	AddPatchUpdateNotificationAppDashboardMutation, AddPatchUpdateNotificationAppDashboardMutationVariables
	>(ADD_PATCH_UPDATE_NOTIFICATION_GQL);

	const { activeUser } = useContext(ActiveUserContext);

	const {
		location,
		history
	} = props;

	const { value: isSuppliersFeatureGateEnabledStatsig } = useStatsigFeatureGate("suppliers_product");
	const { value: isExportsPageFeatureGateEnabledStatsig } = useStatsigFeatureGate("data_export_-_suppliers");

	useEffect(() => {
		if (!getAllKFReportsQuery.loading && !getAllKFReportsQuery.error) {
			const linksByUserRole = getNavLinks({
				activeUser,
				setIsModalOpen,
				setIsPatchUpdateModalOpen,
				kfReports: getAllKFReportsQuery?.data?.getAllKnightFrankReports,
				isSuppliersFeatureGateEnabledStatsig,
				isExportsPageFeatureGateEnabledStatsig
			});

			setNavLinks(linksByUserRole);
		}

		if (
			getAllKFReportsQuery.error
			&& getAllKFReportsQuery.error.message.includes("All premium users must have accepted the premium access policy!")
			&& activeUser?.hasAcceptedPremiumSubscriberPolicy
		) {
			getAllKFReportsQuery.refetch();
		}
	}, [activeUser, getAllKFReportsQuery, isSuppliersFeatureGateEnabledStatsig, isExportsPageFeatureGateEnabledStatsig]);

	const clearTimeouts = () => {
		clearTimeout(firstStageProgressBarLoading);
		clearTimeout(secondStageProgressBarLoading);
		clearTimeout(thirdStageProgressBarLoading);
		clearTimeout(fourthStageProgressBarLoading);
	};

	// If you are changing the transitionTime for the different stages of loading,
	// don't forget to change the milliseconds of the setTimeouts' as well
	// The milliseconds of every timeout should be the sum of
	// the transitionTimes of all previous stages
	const initDefaultProgressBarLoading = () => {
		// Even though the first setTimeout doesn't really wait
		// it is left the way it is because otherwise there are some unwanted side effects
		firstStageProgressBarLoading = setTimeout(() => {
			setProgressBarProps({ progressPercentage: 0, transitionTime: 1, loadingNewPage: false });
		}, 1);

		secondStageProgressBarLoading = setTimeout(() => {
			setProgressBarProps({ progressPercentage: 40, transitionTime: 3, loadingNewPage: false });
		}, 50);

		thirdStageProgressBarLoading = setTimeout(() => {
			setProgressBarProps({ progressPercentage: 60, transitionTime: 4, loadingNewPage: false });
		}, 3000);

		fourthStageProgressBarLoading = setTimeout(() => {
			setProgressBarProps({ progressPercentage: 80, transitionTime: 5, loadingNewPage: false });
		}, 7000);
	};

	const resetProgressBar = (args?: { loadingPageSecion: boolean }) => {
		let loadingPageSecion = null;

		if (args) {
			loadingPageSecion = args.loadingPageSecion;
		}

		// We set loadingNewPage as true by default because
		// when the progress bar is reset, in most cases it means that we have switched pages
		// and we want to start the progress bar again.
		// loadingPageSection is used if we are starting the progress bar for a section
		// instead of a whole page like for example Defined Market or Company Overview.
		// We pass loadingPageSection as true,
		// if we want to hide a section before it is fully loaded
		// and remove the progress bar dissapear without it starting again.
		clearTimeouts();
		setProgressBarProps({ progressPercentage: 0, transitionTime: 0, loadingNewPage: !loadingPageSecion });

		if (isPageLoaded && !loadingPageSecion) {
			setIsPageLoaded(false);
		}

		if (loadedSection) {
			setLoadedSection(null);
		}
	};

	const startProgressBarLoading = (currLoadedSection?: string) => {
		if (currLoadedSection) {
			setLoadedSection(currLoadedSection);
		}

		resetProgressBar();
	};

	useEffect(() => {
		if (progressBarProps.progressPercentage === 0 && progressBarProps.loadingNewPage) {
			initDefaultProgressBarLoading();
		}
	}, [progressBarProps]);

	useEffect(() => {
		if (isPageLoaded) {
			setProgressBarProps({ progressPercentage: 100, transitionTime: 0.8, loadingNewPage: false });
			clearTimeouts();
			setLoadedSection(null);
		}
	}, [isPageLoaded]);

	useEffect(() => {
		setIsModalOpen(false);
		startProgressBarLoading();
	}, [location.pathname]);

	const toggleMobileNav = () => {
		setToggledMobileNav((oldToggleState) => !oldToggleState);
	};

	const breadcrumbs = [
		{
			label: "Home",
			to: "/"
		}
	];

	const addNotificationHandler = (message: string) => {
		addNotification({
			variables: {
				message
			}
		}).then(() => {
			setIsModalOpen(false);
			showSuccess("Successfully sent notification.");
		}).catch(() => {
			showError("Something went wrong while trying to notify users!");
		});
	};

	const addPatchUpdateNotificationHandler = (changelog) => {
		addPatchUpdateNotification({
			variables: {
				changelog
			}
		}).then(() => {
			setIsPatchUpdateModalOpen(false);
			showSuccess("Successfully sent patch updates to all users.");
		}).catch(() => {
			showError("Something went wrong while trying to notify users!");
		});
	};

	const userIsPremiumAndDidNotAcceptPolicy = activeUser?.roles?.find((role) => role.name === authorizationRoles.PREMIUMSUBSCRIBER)
		&& !activeUser?.hasAcceptedPremiumSubscriberPolicy;

	const shouldResetPassword = activeUser?.requirePasswordChange;

	if (shouldResetPassword && !location.state?.userWasRedirected) {
		return (
			<Redirect to={{
				pathname: resetPasswordPath,
				state: {
					isPasswordChangeRequired: shouldResetPassword,
					returnUrl: props.location.state?.returnUrl || props.location.pathname,
					userWasRedirected: true
				}
			}} />
		);
	}

	if (userIsPremiumAndDidNotAcceptPolicy && !location.state?.userWasRedirected) {
		return (
			<Redirect to={{
				pathname: premiumPrivacyPolicyPath,
				state: {
					returnUrl: props.location.pathname,
					userWasRedirected: true
				}
			}} />
		);
	}

	const isSideBarVisible = !userIsPremiumAndDidNotAcceptPolicy;

	return (
		<div className={classes.screenContainer}>
			{/* 
				The DefaultMetaTags component should be used outside of the Suspense component,
				otherwise there is an issue and the page titles don't update correctly 
			*/}
			
			<DefaultMetaTags/>

			<AppHeader
				onBurgerClick={toggleMobileNav}
				isBurgerActive={toggledMobileNav}
				activeUser={activeUser}
				history={history}
			/>

			<AddNotificationPopup
				isShown={isModalOpen}
				onCancelHandler={() => setIsModalOpen(false)}
				onConfirmHandler={addNotificationHandler}
				confirmButtonText="Send"
				headerText="Are you sure you want to send all users a notification?"
				popupText='Upon clicking "Send", all platform users will see a notification on their next visit to Dashboard. The message you enter below will be visible to the users in the Notifications Widget.'
			/>

			<AddNotificationPopup
				isShown={isPatchUpdateModalOpen}
				onCancelHandler={() => setIsPatchUpdateModalOpen(false)}
				onConfirmHandler={addPatchUpdateNotificationHandler}
				confirmButtonText="Send"
				headerText="Are you sure you want to send a patch update to all users?"
				popupText='Upon clicking "Send", all platform users will see a popup with the patch updates on their first log in.'
			/>

			<div className={classes.mainPageContainer}>
				{isSideBarVisible
					? (
						<AppSideBar
							isMobileNavToggled={toggledMobileNav}
							setToggledMobileNav={setToggledMobileNav}
							history={history}
							navConfig={navLinks}
							activeUser={activeUser}
						/>
					)
					: null
				}

				<div className={`${classes.footerContentWrapper} ${!isSideBarVisible ? classes.footerContentWrapperFullWidth : ""}`}>
					<div className={classes.pageContentWrapper}>
						{isSideBarVisible
							? (
								<ProgressBar
									progressPercentage={progressBarProps.progressPercentage}
									transitionTime={progressBarProps.transitionTime}
									fillerStyles={progressBarStyles}
								/>
							)
							: null
						}

						<ProgressBarContext.Provider value={{
							isPageLoaded,
							setIsPageLoaded,
							startProgressBarLoading,
							resetProgressBar,
							loadedSection,
							setLoadedSection,
							setProgressBarStyles
						}}>
							<BreadcrumbsContextComponent>
								<Suspense fallback={null}>
									<Switch>
										<Route
											path={`/${urlEnums.analyticsUrl}`}
											render={(routerProps) => (
												<AnalyticsPage
													{...routerProps}
													activeUser={activeUser}
													breadcrumbs={breadcrumbs}
												/>
											)}
										/>

										<Route
											path={`/${urlEnums.suppliersUrl}`}
											render={(routerProps) => (
												<SuppliersRoutes
													{...routerProps}
													activeUser={activeUser}
													breadcrumbs={breadcrumbs}
												/>
											)}
										/>

										<Route
											path="/dashboard"
											render={(routerProps) => (
												<DashboardRoutes
													{...routerProps}
													activeUser={activeUser}
													breadcrumbs={breadcrumbs}
												/>
											)}
										/>

										<Route
											path="/settings"
											render={(routerProps) => (
												<SettingsRoutes
													{...routerProps}
													activeUser={activeUser}
													breadcrumbs={breadcrumbs}
												/>
											)}
										/>

										<Route
											path={`/${urlEnums.projectPlannerUrl}`}
											render={(routerProps) => (
												<SiteSelector
													{...routerProps}
													activeUser={activeUser}
													breadcrumbs={breadcrumbs}
												/>
											)}
										/>

										<Route
											path="/admin"
											render={(routerProps) => (
												<AdminRoutes
													{...routerProps}
													activeUser={activeUser}
													breadcrumbs={breadcrumbs}
												/>
											)}
										/>

										<Route
											path="/premium-privacy-policy"
											exact
											render={(routerProps) => (
												<PremiumSubscriberPolicyPage
													{...routerProps}
													isSideBarVisible={isSideBarVisible}
													activeUser={activeUser}
												/>
											)}
										/>

										<Route
											path="/"
											render={(routerProps) => (
												<PublicRoutes
													{...routerProps}
													activeUser={activeUser}
													breadcrumbs={breadcrumbs}
													paths={{
														resetPasswordPath
													}}
												/>
											)}
										/>
									</Switch>
								</Suspense>
							</BreadcrumbsContextComponent>
						</ProgressBarContext.Provider>
					</div>

					<AppFooter
						isSideBarVisible={isSideBarVisible}
					/>
				</div>
			</div>
		</div>
	);
};

export default AppDashboard;
